import { PAYMENT_POLICY_CODE_OPTIONS } from '@ibe/constants';
import { Field, FieldInput, FieldLabel, FieldSelect, FieldTextarea, LoadingState } from '@ibe/ui';
import { type ReactNode, Suspense, lazy } from 'react';
import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';

import { PropertySelect } from '../../../components/form/property-select.js';
import { POLICY_STATUS_OPTIONS, type PaymentPolicyFormInput } from '../api/schemas.js';

// Jodit is used only on the policy form screens. Lazy-loading it keeps the
// editor and its stylesheet out of the initial bundle.
const RichTextEditor = lazy(async () => {
  const [{ RichTextEditor: Editor }] = await Promise.all([
    import('../../../components/form/rich-text-editor.js'),
  ]);
  return { default: Editor };
});

/**
 * A payment policy's inputs, independent of what surrounds them.
 *
 * Separated from the pages because create and edit are the same fields in two
 * containers, and a field set that lives inside one of them gets copied the
 * moment the other appears — after which they drift.
 *
 * It takes the three pieces it needs rather than the whole form object, so it
 * cannot reach for `submit` or `reset` and quietly become the thing that owns
 * submission. The container owns that.
 *
 * The sections are the order an operator thinks in: what the policy is, then
 * what the guest is told, then whether it is in force.
 */

export interface PaymentPolicyFieldsProps {
  readonly register: UseFormRegister<PaymentPolicyFormInput>;
  readonly errors: FieldErrors<PaymentPolicyFormInput>;
  /**
   * Needed by `Controller` for the controls the browser does not know about —
   * the property picker, the status select and the editor are all custom.
   */
  readonly control: Control<PaymentPolicyFormInput>;
  /**
   * Which screen this is.
   *
   * Two fields differ, and both differences are the service's: a code cannot
   * be changed after issue, and a policy cannot be moved between properties.
   */
  readonly mode: 'create' | 'edit';
}

function Section({
  title,
  description,
  children,
}: {
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    // A fieldset and legend rather than a heading and a div: this is a group
    // of controls, and the legend is what names the group for a screen reader.
    <fieldset className="space-y-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <legend className="sr-only">{title}</legend>
      <div className="space-y-1">
        <h2 className="text-body font-semibold">{title}</h2>
        {description === undefined ? null : (
          <p className="text-caption text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </fieldset>
  );
}

function Row({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>;
}

export function PaymentPolicyFields({
  register,
  errors,
  control,
  mode,
}: PaymentPolicyFieldsProps): React.JSX.Element {
  return (
    <div className="space-y-6">
      <Section
        title="Identity"
        description="Which property this policy belongs to, and how it is referred to in every rate plan that uses it."
      >
        <Controller
          control={control}
          name="propertyId"
          render={({ field }) => (
            <PropertySelect
              value={field.value}
              onChange={field.onChange}
              error={errors.propertyId?.message}
              required
              readOnly={mode === 'edit'}
            />
          )}
        />

        <Row>
          <Field
            error={errors.code?.message}
            required
            description="When the guest is expected to pay. This appears in exported rate configuration."
          >
            <FieldLabel>Payment type</FieldLabel>
            <Controller
              control={control}
              name="code"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={PAYMENT_POLICY_CODE_OPTIONS}
                  placeholder="Select a payment type"
                  searchable
                />
              )}
            />
          </Field>

          <Field error={errors.name?.message} required>
            <FieldLabel>Policy name</FieldLabel>
            <FieldInput autoComplete="off" {...register('name')} />
          </Field>
        </Row>
      </Section>

      <Section
        title="What the guest is told"
        description="The summary appears in lists and search results. The document is what a guest reads at booking and on their confirmation."
      >
        <Field
          error={errors.description?.message}
          description="One or two plain sentences. No formatting — this is shown where markup cannot be."
        >
          <FieldLabel>Summary</FieldLabel>
          <FieldTextarea rows={2} {...register('description')} />
        </Field>

        <Controller
          control={control}
          name="contentHtml"
          render={({ field }) => (
            <Suspense fallback={<LoadingState label="Loading editor" />}>
              <RichTextEditor
                label="Policy content"
                value={field.value}
                onChange={field.onChange}
                error={errors.contentHtml?.message}
                description="Use Preview to see it exactly as a guest will."
              />
            </Suspense>
          )}
        />
      </Section>

      <Section
        title="Availability"
        description="An inactive policy stays attached to the rate plans that already use it and is offered to no new one."
      >
        <Row>
          <Field error={errors.status?.message}>
            <FieldLabel>Status</FieldLabel>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  options={POLICY_STATUS_OPTIONS}
                  searchable
                />
              )}
            />
          </Field>
        </Row>
      </Section>
    </div>
  );
}
